import { motion } from 'framer-motion';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion';
import type { CardBrand } from '../../../utils/payments/mockCard';
import { BrandLogo } from './BrandLogo';

type CardPreviewProps = {
  number: string;
  name: string;
  expiry: string;
  brand: CardBrand;
};

/**
 * Preview cerimonial do cartão dourado. Gradient gold→bronze→night, com
 * shimmer sutil e hover rotate 3D (respeitando prefers-reduced-motion).
 */
export function CardPreview({ number, name, expiry, brand }: CardPreviewProps) {
  const reduced = usePrefersReducedMotion();

  const displayNumber =
    number.trim() ||
    (brand === 'amex' ? '•••• •••••• •••••' : '•••• •••• •••• ••••');
  const displayName = (name || 'Seu nome aqui').toUpperCase();
  const displayExpiry = expiry || 'MM/AA';

  const hover = reduced ? {} : { rotateY: 4, rotateX: -2, scale: 1.01 };

  return (
    <motion.div
      whileHover={hover}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      style={{ transformStyle: 'preserve-3d' }}
      className="relative mx-auto w-full max-w-sm overflow-hidden rounded-lg border border-gold/50 shadow-goldMedium"
      role="img"
      aria-label="Pré-visualização do cartão de crédito"
    >
      {/* Gradient base */}
      <div className="aspect-[1.586/1] w-full bg-gradient-to-br from-gold via-bronze to-night/90 p-5">
        {/* Top row: chip + bandeira */}
        <div className="flex items-start justify-between">
          <div className="flex h-7 w-10 items-center justify-center rounded-sm bg-gradient-to-br from-champagne/80 to-bronze/60 shadow-inner">
            <div className="grid h-4 w-7 grid-cols-3 grid-rows-2 gap-px overflow-hidden rounded-[1px] bg-bronze/40">
              {Array.from({ length: 6 }, (_, i) => (
                <span key={i} className="bg-gradient-to-br from-champagne to-bronze/70" />
              ))}
            </div>
          </div>
          <BrandLogo brand={brand} />
        </div>

        {/* Número do cartão */}
        <div className="mt-6 sm:mt-8">
          <p
            className="font-mono text-lg tracking-[0.18em] text-night drop-shadow-sm sm:text-xl"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {displayNumber}
          </p>
        </div>

        {/* Bottom row: nome + validade */}
        <div className="mt-6 flex items-end justify-between gap-4">
          <div className="flex min-w-0 flex-col">
            <p className="font-mono text-[9px] uppercase tracking-stamp text-night/60">
              Titular
            </p>
            <p className="truncate font-display text-sm uppercase tracking-wide text-night">
              {displayName}
            </p>
          </div>
          <div className="flex flex-col items-end">
            <p className="font-mono text-[9px] uppercase tracking-stamp text-night/60">
              Validade
            </p>
            <p className="font-mono text-sm text-night">{displayExpiry}</p>
          </div>
        </div>
      </div>

      {/* Shimmer dourado animado (respeita reduced-motion via classe) */}
      {!reduced ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-gold-shimmer-gradient bg-shimmer opacity-30 mix-blend-overlay"
          style={{ animation: 'gold-shimmer 8s linear infinite' }}
        />
      ) : null}
    </motion.div>
  );
}
